/**
 * chatContext.ts — the "bring content from any panel into the central chat"
 * mechanism. This is a defining behavior of the Perplexed-is-Fun framework:
 *
 *   "The chat bar that moves with the pages then allows the user [to] bring in
 *    content or anything from any other page into the main central chat pane."
 *
 * Any panel calls setChatContext({label, detail}) when the user taps an item
 * (a Feed post, a client, a timeline event, a map town, a knowledge hit). The
 * shell shows a "Talking about: <label>" pill; sending routes to the center
 * Chat pane with `detail` injected into the prompt. Tiny external store so we
 * don't prop-drill setters through 11 panels.
 */
import { useSyncExternalStore } from "react";

export interface ChatContextItem {
  /** Shown on the chat-bar pill. */
  label: string;
  /** Injected into the model prompt as the referenced content. */
  detail: string;
}

let current: ChatContextItem | null = null;
const subscribers = new Set<() => void>();

export function setChatContext(item: ChatContextItem | null): void {
  current = item;
  subscribers.forEach((fn) => fn());
}

export function getChatContext(): ChatContextItem | null {
  return current;
}

export function useChatContext(): ChatContextItem | null {
  return useSyncExternalStore(
    (cb) => { subscribers.add(cb); return () => subscribers.delete(cb); },
    () => current,
    () => current,
  );
}
